import React from 'react'
import PropTypes from 'prop-types'
import USER_ORG_ROLES from '../../../utils/constants/userOrgRoles'
import { MANAGER, holdsDuty, isImpliedDuty, normaliseDuties } from '../../../utils/constants/permissionSets'
import CompetitionScopePicker from '../CompetitionScopePicker/CompetitionScopePicker'

const { OWNER, ADMIN, MEMBER } = USER_ORG_ROLES

const CHIP_ON = { border: '1px solid #e8b4b1', background: '#fdeceb', color: '#9e322d' }
// Held through Manager rather than on its own: lighter, and changed only with Manager.
const CHIP_IMPLIED = { border: '1px solid #f3d6d4', background: '#fff8f7', color: '#c0776f' }
const CHIP_OFF = { border: '1px dashed #d0d0d0', background: '#ffffff', color: '#757575' }

const MailIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <rect x='3' y='5' width='18' height='14' rx='2' />
    <path d='M3 7l9 6 9-6' />
  </svg>
)
MailIcon.propTypes = { size: PropTypes.number }

/** One person on the users page: an org member or a pending invitation. */
const OrgUserRow = ({ row, duties, competitions, classes, busy, confirmingRemove, removing, onAccessChange, onToggleDuty, onCompetitionsChange, onRemove, onConfirmRemove, onCancelRemove, onResend }) => {
  const isMember = row.level === MEMBER
  // A Federation's Manager can be limited to some of its competitions.
  const showsCompetitions = isMember && competitions?.length > 0 && normaliseDuties(row.duties).includes(MANAGER)
  const removeLabel = row.pending ? `Cancel the invitation to ${row.email}` : `Remove ${row.name} from the organisation`
  const removeQuestion = row.pending ? `Cancel the invitation to ${row.email}?` : `Remove ${row.name} from the organisation?`

  return (
    <div className={`${classes.grid} ${classes.row}`} style={{ padding: '12px 16px', borderBottom: '1px solid #eeeeee', minHeight: 64, boxSizing: 'border-box' }}>
      <div className={classes.memberCell} style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        {row.pending ? (
          <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', border: '1px dashed #bdbdbd', color: '#8a8a8a', boxSizing: 'border-box' }}>
            <MailIcon />
          </div>
        ) : row.logoUrl ? (
          <img src={row.logoUrl} alt='' style={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 500, background: '#eeeeee', color: '#616161' }}>
            {row.initials}
          </div>
        )}
        {row.pending ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 15, color: '#212121', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.email}</span>
            {row.canResend && (
              <button type='button' className={classes.iconButton} aria-label={`Resend invitation to ${row.email}`} disabled={busy} onClick={onResend} style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, height: 28, padding: '0 8px', border: 0, borderRadius: 4, background: 'transparent', color: '#c4453f', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M20 12a8 8 0 1 1-2.3-5.7' /><path d='M20 4v5h-5' /></svg>
                Resend
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 15, color: '#212121', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
            <span style={{ fontSize: 13, color: '#6b6b6b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.email}</span>
          </div>
        )}
      </div>

      <div className={classes.accessCell}>
        {row.level === OWNER || !row.canEditAccess ? (
          <span title={row.level === OWNER ? "Owner access can't be changed" : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, width: 108, height: 36, padding: '0 10px', boxSizing: 'border-box', fontSize: 14, fontWeight: row.level === OWNER ? 500 : 400, color: '#212121' }}>
            {row.level === OWNER && (
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><rect x='5' y='11' width='14' height='10' rx='2' /><path d='M8 11V8a4 4 0 0 1 8 0v3' /></svg>
            )}
            {row.level}
          </span>
        ) : (
          <select className={`${classes.select} ${classes.accessSelect}`} aria-label={`Access level for ${row.email}`} value={row.level} disabled={busy} onChange={(e) => onAccessChange(e.target.value)} style={{ width: 108, height: 36, padding: '0 32px 0 10px', boxSizing: 'border-box', border: '1px solid #d6d6d6', borderRadius: 4, backgroundColor: '#ffffff', fontSize: 14, color: '#212121', cursor: 'pointer' }}>
            <option value={ADMIN}>{ADMIN}</option>
            <option value={MEMBER}>{MEMBER}</option>
          </select>
        )}
      </div>

      {!confirmingRemove && (
        <div className={classes.dutiesCell} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {isMember ? (
            duties.map((duty) => {
              const on = holdsDuty(row.duties, duty.id)
              // A Manager holds every duty: the others show lighter and change only with Manager.
              const implied = isImpliedDuty(row.duties, duty.id)
              // Someone who cannot edit this row still sees the duties held; the rest are hidden.
              if (!row.canEditDuties && !on) return null
              return (
                <button key={duty.id} type='button' className={classes.chip} aria-pressed={on} disabled={busy || !row.canEditDuties || implied} title={implied ? 'Included with Manager' : undefined} onClick={() => onToggleDuty(duty.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 16, fontSize: 13, fontWeight: 500, cursor: 'pointer', ...(implied ? CHIP_IMPLIED : on ? CHIP_ON : CHIP_OFF) }}>
                  {duty.label}
                </button>
              )
            })
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b6b6b' }}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z' /></svg>
              {row.level === OWNER ? 'Full access' : 'Elevated access'}
            </span>
          )}
          {row.draft && (
            <span role='status' style={{ fontSize: 13, color: '#9e322d' }}>Pick a duty to save</span>
          )}
          {showsCompetitions && (
            <CompetitionScopePicker
              competitions={competitions}
              selected={row.managerCompetitions || []}
              disabled={busy}
              readOnly={!row.canEditDuties}
              label={`Competitions ${row.email} manages`}
              onChange={onCompetitionsChange}
            />
          )}
        </div>
      )}

      {confirmingRemove ? (
        // Confirmed in place, over the duties, instead of a dialog.
        <div role='group' aria-label={removeQuestion} onKeyDown={(e) => e.key === 'Escape' && onCancelRemove()} className={classes.confirmCell}>
          <span style={{ fontSize: 14, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{removeQuestion}</span>
          <button type='button' className={classes.iconButton} autoFocus disabled={removing} onClick={onCancelRemove} style={{ flexShrink: 0, height: 36, padding: '0 14px', border: '1px solid #d6d6d6', borderRadius: 4, background: 'transparent', fontSize: 14, color: '#212121', cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancel
          </button>
          <button type='button' className={classes.deleteButton} disabled={removing} onClick={onConfirmRemove} style={{ flexShrink: 0, height: 36, padding: '0 16px', border: 0, borderRadius: 4, background: '#c9453f', color: '#ffffff', fontSize: 14, fontWeight: 500, cursor: removing ? 'default' : 'pointer', opacity: removing ? 0.7 : 1, fontFamily: 'inherit' }}>
            {removing ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      ) : (
        <div className={classes.actionCell} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {row.canRemove && (
            <button type='button' className={`${classes.iconButton} ${classes.removeButton}`} aria-label={removeLabel} title={removeLabel} disabled={busy} onClick={onRemove} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, border: 0, borderRadius: 4, background: 'transparent', cursor: 'pointer' }}>
              <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'><path d='M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3' /></svg>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

OrgUserRow.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    logoUrl: PropTypes.string,
    initials: PropTypes.string,
    level: PropTypes.string,
    duties: PropTypes.arrayOf(PropTypes.string),
    managerCompetitions: PropTypes.arrayOf(PropTypes.string),
    draft: PropTypes.bool,
    pending: PropTypes.bool,
    canEditAccess: PropTypes.bool,
    canEditDuties: PropTypes.bool,
    canRemove: PropTypes.bool,
    canResend: PropTypes.bool,
  }).isRequired,
  duties: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })).isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  busy: PropTypes.bool,
  confirmingRemove: PropTypes.bool,
  removing: PropTypes.bool,
  onAccessChange: PropTypes.func.isRequired,
  onToggleDuty: PropTypes.func.isRequired,
  competitions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  onCompetitionsChange: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  onConfirmRemove: PropTypes.func.isRequired,
  onCancelRemove: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
}

export default OrgUserRow
